import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { SCHEDULE_SLOT_KEYS, SCHEDULE_SLOT_LABELS } from './constants';
import { getSlotChipSx } from './calendarChipColors';
import { getResolvedSlotKeysForDay } from './storage';

function toggleSlot(prev, key) {
  const set = new Set(prev);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  return SCHEDULE_SLOT_KEYS.filter(sk => set.has(sk));
}

/**
 * Popup to edit a single calendar day: date + time slot chips + Save / Cancel.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   date: import('dayjs').Dayjs | null,
 *   liveInfo: Record<string, { working?: boolean, slots?: string[] }>,
 *   onSave: (nextLiveInfo: Record<string, { working?: boolean, slots?: string[] }>) => void,
 * }} props
 */
function CalendarDaySlotsDialog({ open, onClose, date, liveInfo, onSave }) {
  const [slotSelection, setSlotSelection] = useState([]);

  useEffect(() => {
    if (!open || !date || !dayjs.isDayjs(date)) return;
    setSlotSelection(getResolvedSlotKeysForDay(liveInfo, date));
  }, [open, date, liveInfo]);

  const handleSave = () => {
    if (!date || !dayjs.isDayjs(date)) return;
    const key = date.format('YYYY-MM-DD');
    const slots = SCHEDULE_SLOT_KEYS.filter(sk => slotSelection.includes(sk));
    const nextLive = { ...liveInfo };
    nextLive[key] =
      slots.length > 0 ? { working: true, slots } : { working: false };
    onSave(nextLive);
    onClose();
  };

  const dateLabel =
    date && dayjs.isDayjs(date) ? date.format('dddd, MMMM D, YYYY') : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper">
      <DialogTitle>Day schedule</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          Date
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {dateLabel}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Time slots
        </Typography>
        <Stack direction="column" spacing={0.75} sx={{ width: '100%' }}>
          {SCHEDULE_SLOT_KEYS.map(key => {
            const checked = slotSelection.includes(key);
            return (
              <Stack
                key={key}
                direction="row"
                alignItems="center"
                spacing={0.75}
                component="label"
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: '100%',
                }}>
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() =>
                    setSlotSelection(prev => toggleSlot(prev, key))
                  }
                  inputProps={{
                    'aria-label': `${SCHEDULE_SLOT_LABELS[key]} time slot`,
                  }}
                />
                <Chip
                  label={
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        display: 'block',
                        textAlign: 'left',
                        color: 'inherit',
                        ml: 1,
                        mt: 0.1,
                      }}>
                      {SCHEDULE_SLOT_LABELS[key]}
                    </Typography>
                  }
                  size="small"
                  variant="filled"
                  sx={{
                    ...getSlotChipSx(key),
                    flex: 1,
                    minWidth: '100px',
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CalendarDaySlotsDialog;
